import React, { useState, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  MessageCircle, Mail, Phone, Calendar, Tag, 
  TrendingUp, X, Send, Bot, User, Sparkles,
  GripVertical, Image as ImageIcon, Loader2,
  Filter, Megaphone, SpellCheck, Languages, 
  Paperclip, File, Video, Minus, Maximize2
} from 'lucide-react';
import { Header } from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CustomerProfile } from '../components/CustomerProfile';
import { QuoteCard, parseMessageForQuotes } from '../components/QuoteCard';
import { useChatContextMenu } from '../components/ChatContextMenu';
import { useApp } from '../contexts/AppContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { cn } from '../lib/utils';
import axios from 'axios';
import { noahToast } from '../lib/noahToast';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  lead: { 
    label: 'Leads', 
    gradient: 'bg-brand-deep',
    glow: 'rgba(148, 163, 184, 0.6)',
    ring: 'ring-slate-400/50'
  },
  prospect: { 
    label: 'Prospectos', 
    gradient: 'bg-brand-green',
    glow: 'rgba(59, 130, 246, 0.6)',
    ring: 'ring-brand-green/50'
  },
  qualified: { 
    label: 'Calificados', 
    gradient: 'bg-brand-deep',
    glow: 'rgba(85, 153, 117, 0.6)',
    ring: 'ring-violet-400/50'
  },
  negotiation: { 
    label: 'Negociación', 
    gradient: 'bg-brand-copper',
    glow: 'rgba(245, 158, 11, 0.6)',
    ring: 'ring-amber-400/50'
  },
  closed_won: { 
    label: 'Ganados', 
    gradient: 'bg-brand-green',
    glow: 'rgba(16, 185, 129, 0.6)',
    ring: 'ring-emerald-400/50'
  },
  closed_lost: { 
    label: 'Perdidos', 
    gradient: 'bg-destructive',
    glow: 'rgba(239, 68, 68, 0.6)',
    ring: 'ring-red-400/50'
  },
  inquiry: { 
    label: 'Consultas', 
    gradient: 'bg-brand-copper',
    glow: 'rgba(6, 182, 212, 0.6)',
    ring: 'ring-brand-green/50',
    dashed: true
  }
};

// Glass Bubble Component with realistic effects
const GlassBubble = ({ customer, onClick, isDragging, isOverlay }) => {
  const config = statusConfig[customer.status] || statusConfig.lead;
  const hasNewMessage = customer.has_new_message || false;
  
  return (
    <div
      data-testid={`bubble-${customer.id}`}
      onClick={() => !isDragging && onClick?.(customer)}
      className={cn(
        "group relative cursor-grab active:cursor-grabbing",
        "transition-all duration-300 ease-out",
        isDragging && "opacity-50",
        isOverlay && "cursor-grabbing scale-105"
      )}
    >
      {/* Pulse ring for new messages */}
      {hasNewMessage && (
        <>
          <div className="absolute inset-0 rounded-2xl ring-2 ring-primary/50 animate-pulse pointer-events-none" />
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-full z-20 shadow-lg shadow-primary/50 flex items-center justify-center animate-bounce">
            <span className="text-[9px] text-white font-bold">!</span>
          </div>
        </>
      )}

      {/* Outer glow */}
      <div 
        className={cn(
          "absolute inset-0 rounded-2xl blur-xl -z-10 transition-opacity group-hover:opacity-80",
          hasNewMessage ? "opacity-80 animate-pulse" : "opacity-60"
        )}
        style={{ backgroundColor: config.glow, opacity: 0.28 }}
      />
      
      {/* Glass card */}
      <div className={cn(
        "relative p-4 rounded-2xl overflow-hidden",
        "bg-card/90 backdrop-blur-xl",
        "border border-border/50",
        "shadow-lg hover:shadow-xl",
        "hover:border-primary/30 hover:-translate-y-1",
        "transition-all duration-300"
      )}>
        {/* Light reflection */}
        <div className="absolute top-0 left-0 right-0 h-1/3 bg-primary/10 rounded-t-2xl pointer-events-none" />
        
        {/* Bubble shine effect */}
        <div className="absolute -top-10 -right-10 w-20 h-20 bg-primary/10 rounded-full blur-2xl pointer-events-none" />
        
        {/* Content */}
        <div className="relative z-10 flex items-center gap-3">
          {/* Drag handle */}
          <div className="opacity-0 group-hover:opacity-60 transition-opacity">
            <GripVertical className="w-4 h-4 text-white/60" />
          </div>
          
          {/* Avatar bubble */}
          <div className={cn(
            "relative w-12 h-12 rounded-full flex items-center justify-center",
            config.gradient,
            "shadow-lg ring-2", config.ring
          )}>
            <span className="text-lg font-bold text-white drop-shadow-md">
              {customer.name?.charAt(0)?.toUpperCase()}
            </span>
            
            {/* Avatar shine */}
            <div className="absolute top-1 left-1 w-3 h-3 bg-white/40 rounded-full blur-sm" />
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-foreground truncate">
              {customer.name}
            </h4>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {customer.email && (
                <span className="truncate max-w-[120px]">{customer.email}</span>
              )}
            </div>
          </div>
          
          {/* Platform indicator */}
          <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.8)]" />
        </div>
      </div>
    </div>
  );
};

// Sortable bubble wrapper
const SortableBubble = ({ customer, onClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: customer.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <GlassBubble customer={customer} onClick={onClick} isDragging={isDragging} />
    </div>
  );
};

// Status Column
const StatusColumn = ({ status, customers, onSelectCustomer }) => {
  const config = statusConfig[status];
  
  return (
    <div 
      data-testid={`column-${status}`}
      className="flex-1 min-w-[280px] max-w-[320px]"
    >
      {/* Column header */}
      <div className={cn(
        "mb-4 p-3 rounded-xl",
        "bg-primary/10",
        "backdrop-blur-md border border-white/20"
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={cn(
              "w-3 h-3 rounded-full shadow-lg",
              config.gradient
            )} />
            <span className="font-semibold text-foreground">{config.label}</span>
          </div>
          <Badge variant="outline" className="bg-white/10 border-white/20 text-foreground">
            {customers.length}
          </Badge>
        </div>
      </div>
      
      {/* Customers list */}
      <SortableContext items={customers.map(c => c.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-3 min-h-[200px] p-2 rounded-xl bg-white/5 border border-white/10">
          {customers.map((customer) => (
            <SortableBubble
              key={customer.id}
              customer={customer}
              onClick={onSelectCustomer}
            />
          ))}
          {customers.length === 0 && (
            <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
              Arrastra clientes aquí
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
};

// Detail Panel with Chat and Info
const CustomerDetailPanel = ({ customer, onClose, chats, messages, onSendMessage, onGenerateImage, onOpenProfile }) => {
  const [newMessage, setNewMessage] = useState('');
  const [activeTab, setActiveTab] = useState('chat');
  const [imagePrompt, setImagePrompt] = useState('');
  const [generatingImage, setGeneratingImage] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const { openMenu, ContextMenuPortal } = useChatContextMenu();
  const { addChatBubble } = useWorkspace();
  const config = statusConfig[customer?.status] || statusConfig.lead;
  const customerChat = chats.find(c => c.customer_id === customer?.id);

  if (!customer) return null;

  const handleSend = (customMsg) => {
    const msg = customMsg || newMessage;
    if (msg.trim() && customerChat) {
      onSendMessage(customerChat.id, msg);
      if (!customMsg) setNewMessage('');
    }
  };

  const handleGenerateImage = async () => {
    if (!imagePrompt.trim()) return;
    setGeneratingImage(true);
    try {
      const res = await axios.post(`${API}/generate-image`, {
        prompt: imagePrompt,
        product_context: customer.notes,
        style: "profesional, moderno, alta calidad"
      });
      setGeneratedImage(res.data.image_base64);
    } catch (error) {
      console.warn('Image generation error:', error?.message);
    } finally {
      setGeneratingImage(false);
    }
  };

  return (
    <div 
      data-testid="detail-panel"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div 
        className={cn(
          "w-full max-w-6xl h-[90vh] overflow-hidden animate-scale-in",
          "bg-primary/95",
          "backdrop-blur-2xl rounded-3xl",
          "border border-white/20",
          "shadow-[0_25px_80px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.1)]"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={cn(
          "h-20 px-6 flex items-center justify-between",
          config.gradient
        )}>
          <div className="flex items-center gap-4">
            <div className={cn(
              "relative w-14 h-14 rounded-full flex items-center justify-center",
              "bg-white/20 backdrop-blur shadow-lg ring-2 ring-white/40"
            )}>
              <span className="text-2xl font-bold text-white">
                {customer.name?.charAt(0)?.toUpperCase()}
              </span>
              <div className="absolute top-1 left-2 w-4 h-4 bg-white/30 rounded-full blur-sm" />
            </div>
            <div className="text-white">
              <h2 
                data-testid="pipeline-customer-name"
                className="text-xl font-bold drop-shadow-md cursor-pointer hover:text-violet-200 transition-colors"
                onClick={() => onOpenProfile?.(customer.id)}
              >{customer.name}</h2>
              <div className="flex items-center gap-2 text-white/80 text-sm">
                <Badge className="bg-white/20 text-white border-none">
                  {config.label}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              data-testid="minimize-to-bubble"
              variant="ghost"
              size="icon"
              onClick={() => { 
                if (customerChat) addChatBubble({ id: customerChat.id, customer: customer, customer_id: customer.id, campaign_id: customerChat.campaign_id, last_message: customerChat.last_message });
                onClose();
              }}
              className="text-white hover:bg-white/20"
              title="Minimizar a burbuja"
            >
              <Minus className="w-5 h-5" />
            </Button>
            <Button
              data-testid="close-panel"
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-white hover:bg-white/20"
            >
              <X className="w-6 h-6" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col sm:flex-row h-[calc(90vh-5rem)]">
          {/* Left: Chat */}
          <div className="flex-1 flex flex-col border-b sm:border-b-0 sm:border-r border-white/10 min-w-0 min-h-0">
            <div className="p-3 sm:p-4 border-b border-white/10 flex items-center gap-2">
              <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              <h3 className="font-semibold text-sm sm:text-base">Conversacion</h3>
            </div>
            
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {messages.map((msg, idx) => (
                  <div
                    key={msg.id || `msg-${idx}`}
                    onContextMenu={(e) => openMenu(e, msg)}
                    className={cn(
                      "flex gap-3 animate-fade-in cursor-default",
                      msg.sender === 'customer' ? "" : "flex-row-reverse"
                    )}
                  >
                    <Avatar className="h-8 w-8 flex-shrink-0">
                      <AvatarFallback className={cn(
                        msg.sender === 'ai' 
                          ? "bg-primary"
                          : msg.sender === 'customer'
                          ? "bg-brand-green"
                          : "bg-emerald-500"
                      )}>
                        {msg.sender === 'ai' ? (
                          <Bot className="w-4 h-4 text-white" />
                        ) : msg.sender === 'customer' ? (
                          <User className="w-4 h-4 text-white" />
                        ) : (
                          <span className="text-white text-xs">Tú</span>
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <div
                      className={cn(
                        "max-w-[70%] rounded-2xl px-4 py-2.5 text-sm",
                        msg.sender === 'customer'
                          ? "bg-muted rounded-tl-sm"
                          : msg.sender === 'ai'
                          ? "bg-primary/20 border border-violet-500/30 rounded-tr-sm"
                          : "bg-primary text-primary-foreground rounded-tr-sm"
                      )}
                    >
                      {msg.sender === 'ai' && (
                        <div className="flex items-center gap-1 text-xs text-brand-deep dark:text-brand-deep dark:text-brand-green mb-1">
                          <Sparkles className="w-3 h-3" />
                          <span>Noah IA</span>
                        </div>
                      )}
                      {msg.sender === 'ai' && msg.content?.includes('[COTIZACION]') ? (
                        <div className="space-y-2">
                          {parseMessageForQuotes(msg.content).map((part, pi) =>
                            part.type === 'quote' ? (
                              <QuoteCard key={pi} rawText={part.content} />
                            ) : part.content ? (
                              <p key={pi}>{part.content}</p>
                            ) : null
                          )}
                        </div>
                      ) : (
                        <p>{msg.content}</p>
                      )}
                    </div>
                  </div>
                ))}
                {messages.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Sin mensajes aún</p>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Message input with full tools */}
            <div className="p-3 sm:p-4 border-t border-white/10">
              <div className="flex items-center gap-1 mb-2 flex-wrap">
                <button onClick={() => {
                  const el = document.querySelector('[data-testid="message-input"]');
                  if (el) { el.setAttribute('spellcheck', 'true'); el.focus(); }
                  toast.info('Ortografia activada');
                }} className="p-1.5 rounded-lg hover:bg-violet-500/10 text-muted-foreground hover:text-brand-deep dark:text-brand-deep dark:text-brand-green transition-colors" title="Ortografia">
                  <SpellCheck className="w-4 h-4" />
                </button>
                <button onClick={async () => {
                  if (!newMessage.trim()) return;
                  toast.info('Traduciendo...');
                  try {
                    const targetLang = document.documentElement.lang === 'es' ? 'English' : 'Espanol';
                    const res = await axios.post(`${API}/ai-assistant`, { message: `Translate to ${targetLang}. Only return translation: "${newMessage}"`, session_id: 'translator' });
                    if (res.data?.response) setNewMessage(res.data.response);
                  } catch (err) { console.warn('Translate error:', err?.message); toast.error('Error al traducir'); }
                }} className="p-1.5 rounded-lg hover:bg-brand-green/10 text-muted-foreground hover:text-brand-deep dark:text-brand-green transition-colors" title="Traducir">
                  <Languages className="w-4 h-4" />
                </button>
                <div className="w-px h-4 bg-white/10 mx-0.5" />
                <label className="p-1.5 rounded-lg hover:bg-brand-green/10 text-muted-foreground hover:text-brand-deep dark:text-brand-green cursor-pointer transition-colors" title="Imagen">
                  <ImageIcon className="w-4 h-4" />
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file || !customerChat) return;
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      const res = await axios.post(`${API}/files/upload?chat_id=${customerChat.id}&customer_id=${customer.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
                      handleSend(`[Imagen] ${res.data.filename}\n${url}`);
                    } catch (err) { console.warn('Upload error:', err?.message); toast.error('Error al subir'); }
                    e.target.value = '';
                  }} />
                </label>
                <label className="p-1.5 rounded-lg hover:bg-emerald-500/10 text-muted-foreground hover:text-emerald-700 dark:text-emerald-400 cursor-pointer transition-colors" title="Video/Audio">
                  <Video className="w-4 h-4" />
                  <input type="file" accept="video/*,audio/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file || !customerChat) return;
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      const res = await axios.post(`${API}/files/upload?chat_id=${customerChat.id}&customer_id=${customer.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
                      handleSend(`[Media] ${res.data.filename}\n${url}`);
                    } catch (err) { console.warn('Upload error:', err?.message); toast.error('Error al subir'); }
                    e.target.value = '';
                  }} />
                </label>
                <label className="p-1.5 rounded-lg hover:bg-amber-500/10 text-muted-foreground hover:text-amber-700 dark:text-amber-400 cursor-pointer transition-colors" title="Documento">
                  <File className="w-4 h-4" />
                  <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file || !customerChat) return;
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      const res = await axios.post(`${API}/files/upload?chat_id=${customerChat.id}&customer_id=${customer.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
                      handleSend(`[Documento] ${res.data.filename}\n${url}`);
                    } catch (err) { console.warn('Upload error:', err?.message); toast.error('Error al subir'); }
                    e.target.value = '';
                  }} />
                </label>
              </div>
              <div className="flex gap-2">
                <Input
                  data-testid="message-input"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                  placeholder="Escribe un mensaje..."
                  className="flex-1 bg-white/10 border-white/20 text-[16px] sm:text-sm"
                  disabled={!customerChat}
                  spellCheck="true"
                />
                <Button
                  data-testid="send-btn"
                  onClick={() => handleSend()}
                  disabled={!newMessage.trim() || !customerChat}
                  className="bg-primary"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Right: Info & Image Gen */}
          <div className="w-full sm:w-96 flex flex-col max-h-[40vh] sm:max-h-none overflow-auto">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
              <TabsList className="mx-4 mt-4 grid grid-cols-3 bg-white/10">
                <TabsTrigger value="info">Info</TabsTrigger>
                <TabsTrigger value="ai">IA</TabsTrigger>
                <TabsTrigger value="image">Imagen</TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="flex-1 overflow-auto p-4 space-y-4">
                <Card className="bg-white/10 border-white/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Contacto
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {customer.email && (
                      <div className="flex items-center gap-3 text-sm">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <span>{customer.email}</span>
                      </div>
                    )}
                    {customer.phone && (
                      <div className="flex items-center gap-3 text-sm">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <span>{customer.phone}</span>
                      </div>
                    )}
                    {customer.created_at && (
                      <div className="flex items-center gap-3 text-sm">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span>Desde: {new Date(customer.created_at).toLocaleDateString('es-ES')}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {customer.notes && (
                  <Card className="bg-white/10 border-white/20">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Notas</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{customer.notes}</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="ai" className="flex-1 overflow-auto p-4 space-y-4">
                <Card className="bg-primary/10 border-violet-500/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
                      Resumen IA
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      {customer.ai_summary || 'Noah generará un resumen automático basado en las conversaciones.'}
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-white/10 border-white/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Datos Extraídos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {Object.keys(customer.extracted_data || {}).length > 0 ? (
                      <div className="space-y-2">
                        {Object.entries(customer.extracted_data).map(([key, value]) => (
                          <div key={key} className="flex justify-between text-sm">
                            <span className="text-muted-foreground capitalize">{key}</span>
                            <span>{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        La IA extraerá información de las conversaciones.
                      </p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="image" className="flex-1 overflow-auto p-4 space-y-4">
                <Card className="bg-primary/10 border-fuchsia-500/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <ImageIcon className="w-4 h-4 text-fuchsia-700 dark:text-fuchsia-300" />
                      Generar Imagen (Nano Banana)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Input
                      data-testid="image-prompt"
                      value={imagePrompt}
                      onChange={(e) => setImagePrompt(e.target.value)}
                      placeholder="Describe la imagen del producto..."
                      className="bg-white/10 border-white/20"
                    />
                    <Button
                      data-testid="generate-image-btn"
                      onClick={handleGenerateImage}
                      disabled={!imagePrompt.trim() || generatingImage}
                      className="w-full bg-primary"
                    >
                      {generatingImage ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generando...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generar Imagen
                        </>
                      )}
                    </Button>
                    
                    {generatedImage && (
                      <div className="mt-4 space-y-3">
                        <div className="rounded-xl overflow-hidden border border-white/20">
                          <img 
                            src={`data:image/png;base64,${generatedImage}`}
                            alt="Generated"
                            className="w-full h-auto"
                          />
                        </div>
                        {customerChat && (
                          <Button
                            data-testid="send-image-chat-btn"
                            onClick={() => {
                              onSendMessage(customerChat.id, `[Imagen generada por Noah IA] Mira esta imagen que preparé para ti!`);
                            }}
                            variant="outline"
                            className="w-full gap-2 border-emerald-500/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10"
                          >
                            <Send className="w-4 h-4" /> Enviar imagen al chat
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
      <ContextMenuPortal onAction={(action, msg) => {
        if (action === 'reply') setNewMessage(`> ${msg?.content?.slice(0,60)}...\n`);
        else if (action === 'translate') {
          axios.post(`${API}/ai-assistant`, { message: `Translate to English. Only return translation: "${msg?.content}"`, session_id: 'translator' })
            .then(r => { if (r.data?.response) toast.info(r.data.response); })
            .catch(() => toast.error('Error'));
        }
        else if (action === 'favorite') toast.success('Marcado como favorito');
        else if (action === 'save') toast.success('Mensaje guardado');
        else if (action === 'spellcheck') {
          axios.post(`${API}/ai-assistant`, { message: `Corrige la ortografia de este texto, solo devuelve el texto corregido: "${msg?.content}"`, session_id: 'spellcheck' })
            .then(r => { if (r.data?.response) toast.info(r.data.response); })
            .catch(() => toast.error('Error'));
        }
        else toast.info(`Accion: ${action}`);
      }} />
    </div>
  );
};

// Main Kanban Component
const CustomerKanban = () => {
  const { 
    customers, chats, campaigns, updateCustomer,
    fetchCustomers, fetchChats, getChatMessages, sendMessage 
  } = useApp();
  
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerMessages, setCustomerMessages] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [customersByStatus, setCustomersByStatus] = useState({});
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [profileCustomerId, setProfileCustomerId] = useState(null);
  const { openWorkspace } = useWorkspace();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchCustomers();
    fetchChats();
  }, [fetchCustomers, fetchChats]);

  useEffect(() => {
    // Filter customers by selected campaign
    const filtered = selectedCampaign === 'all' 
      ? customers 
      : customers.filter(c => c.campaign_id === selectedCampaign);
    
    // Enrich customers with has_new_message from chats
    const enriched = filtered.map(c => {
      const customerChat = chats.find(ch => ch.customer_id === c.id);
      return { ...c, has_new_message: customerChat?.unread_count > 0 || false };
    });
    
    // Group customers by status
    const grouped = {
      lead: enriched.filter(c => c.status === 'lead'),
      prospect: enriched.filter(c => c.status === 'prospect'),
      qualified: enriched.filter(c => c.status === 'qualified'),
      negotiation: enriched.filter(c => c.status === 'negotiation'),
      closed_won: enriched.filter(c => c.status === 'closed_won'),
      closed_lost: enriched.filter(c => c.status === 'closed_lost'),
      inquiry: enriched.filter(c => c.status === 'inquiry'),
    };
    setCustomersByStatus(grouped);
  }, [customers, selectedCampaign, chats]);

  useEffect(() => {
    const loadMessages = async () => {
      if (selectedCustomer) {
        const customerChat = chats.find(c => c.customer_id === selectedCustomer.id);
        if (customerChat) {
          const msgs = await getChatMessages(customerChat.id);
          setCustomerMessages(msgs);
        } else {
          setCustomerMessages([]);
        }
      }
    };
    loadMessages();
  }, [selectedCustomer, chats, getChatMessages]);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeCustomer = customers.find(c => c.id === active.id);
    if (!activeCustomer) return;

    // Find which column the item was dropped in
    const overColumnId = Object.keys(customersByStatus).find(status => 
      customersByStatus[status].some(c => c.id === over.id)
    );

    // Or check if dropped on a column directly
    const targetStatus = over.id.toString().replace('column-', '');
    const newStatus = overColumnId || (statusConfig[targetStatus] ? targetStatus : null);

    if (newStatus && newStatus !== activeCustomer.status) {
      try {
        await updateCustomer(activeCustomer.id, { status: newStatus });
        if (newStatus === 'closed_won') {
          let msg = `${activeCustomer.name || 'El cliente'} pasó a Ganado`;
          try {
            const { data: st } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/deals/streak`);
            if (st?.streak >= 2) msg = `Racha de ${st.streak} cierres seguidos · $${Number(st.streak_value || 0).toLocaleString()}`;
          } catch { /* la racha es un extra */ }
          noahToast('happy', '¡Trato ganado! 🎉', msg);
        }
      } catch (error) {
        // Error handled by toast
      }
    }
  };

  const handleSendMessage = async (chatId, content) => {
    const chat = chats.find(c => c.id === chatId);
    if (!chat) return;
    
    try {
      const result = await sendMessage(chatId, {
        content,
        sender: 'agent',
        platform: chat.platform
      });
      
      setCustomerMessages(prev => {
        const newMessages = [...prev, result.message];
        if (result.ai_response) {
          newMessages.push(result.ai_response);
        }
        return newMessages;
      });
    } catch (error) {
      // Error handled by toast
    }
  };

  const activeCustomer = activeId ? customers.find(c => c.id === activeId) : null;
  
  const filteredTotal = Object.values(customersByStatus).reduce((sum, arr) => sum + arr.length, 0);
  const selectedCampaignName = selectedCampaign === 'all' ? null : campaigns.find(c => c.id === selectedCampaign)?.name;

  return (
    <div data-testid="kanban-page" className="min-h-screen">
      <Header 
        title="Pipeline de Clientes" 
        subtitle={selectedCampaignName ? `Campaña: ${selectedCampaignName} (${filteredTotal} clientes)` : `Arrastra y suelta para cambiar estado (${filteredTotal} clientes)`}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
              <SelectTrigger className="w-[220px] h-9" data-testid="campaign-filter">
                <SelectValue placeholder="Filtrar por campaña" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="flex items-center gap-2">
                    <Megaphone className="w-3 h-3" />
                    Todas las campañas
                  </span>
                </SelectItem>
                {campaigns.map(camp => (
                  <SelectItem key={camp.id} value={camp.id}>
                    <span className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: camp.color }} />
                      {camp.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Header>
      
      <div className="p-6">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-4 overflow-x-auto pb-4 no-scrollbar pipeline-columns">
            {Object.entries(customersByStatus).map(([status, statusCustomers]) => (
              <StatusColumn
                key={status}
                status={status}
                customers={statusCustomers}
                onSelectCustomer={setSelectedCustomer}
              />
            ))}
          </div>

          <DragOverlay>
            {activeCustomer && (
              <GlassBubble customer={activeCustomer} isOverlay />
            )}
          </DragOverlay>
        </DndContext>

        {customers.length === 0 && (
          <div className="text-center py-16">
            <div className={cn(
              "w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center",
              "bg-primary/20",
              "shadow-[0_0_40px_rgba(85,153,117,0.3)]"
            )}>
              <User className="w-12 h-12 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Sin clientes</h3>
            <p className="text-muted-foreground">
              Carga datos de demostración desde el Dashboard
            </p>
          </div>
        )}
      </div>

      {selectedCustomer && (
        <CustomerDetailPanel
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
          chats={chats}
          messages={customerMessages}
          onSendMessage={handleSendMessage}
          onOpenProfile={(customerId) => setProfileCustomerId(customerId)}
        />
      )}

      {profileCustomerId && (
        <CustomerProfile
          customerId={profileCustomerId}
          onClose={() => setProfileCustomerId(null)}
          onNavigate={(page) => { setProfileCustomerId(null); openWorkspace(page); }}
        />
      )}
    </div>
  );
};

export default CustomerKanban;
